import axiosInstance from "@/services/axiosInstance";
import type { UserTokenResponse } from "@/types/api/data-contracts";

const TEMP_TOKEN_PARAM = "temp_token";
const AUTH_TOKEN_KEY = "userAuthToken";
const LAST_LOGIN_KEY = "last_login";

/**
 * Read the inbound `temp_token` from the URL fragment first, falling back to
 * the query string. The new buyer app sends the token in the fragment (so it
 * never reaches the server), but the legacy MoR masquerade flow still hands
 * it to us via `?temp_token=`, so the query fallback is required.
 */
const readInboundTempToken = (): string | null => {
    const { hash, search } = window.location;
    if (hash.includes(`${TEMP_TOKEN_PARAM}=`)) {
        const inner = hash.startsWith("#") ? hash.slice(1) : hash;
        const fromHash = new URLSearchParams(inner).get(TEMP_TOKEN_PARAM);
        if (fromHash) return fromHash;
    }
    return new URLSearchParams(search).get(TEMP_TOKEN_PARAM);
};

/**
 * Strip the consumed `temp_token` from both the query string and the
 * fragment. Preserves `window.history.state` so Vue Router's scroll-position
 * metadata isn't wiped, and leaves any unrelated fragment content intact
 * (e.g. `#section-anchor`). Runs regardless of exchange outcome — leaving the
 * param on a now-consumed token would leak it into history, analytics, and
 * any link the user might share.
 */
const stripInboundTempToken = (): void => {
    const { pathname, search, hash } = window.location;

    const params = new URLSearchParams(search);
    params.delete(TEMP_TOKEN_PARAM);
    const newSearch = params.toString();

    let newHash = hash;
    if (hash.includes(`${TEMP_TOKEN_PARAM}=`)) {
        const inner = hash.startsWith("#") ? hash.slice(1) : hash;
        const remaining = inner
            .split("&")
            .filter((part) => !part.startsWith(`${TEMP_TOKEN_PARAM}=`))
            .join("&");
        newHash = remaining ? `#${remaining}` : "";
    }

    const newUrl = pathname + (newSearch ? `?${newSearch}` : "") + newHash;
    window.history.replaceState(window.history.state, "", newUrl);
};

/**
 * Read `temp_token` from the current URL, exchange it for a real auth token,
 * and persist the result in localStorage. Strips the param from the URL
 * regardless of outcome so the consumed token does not leak into history,
 * analytics, or anything the user might paste.
 */
export const exchangeTempTokenIfPresent = async (): Promise<void> => {
    const tempToken = readInboundTempToken();
    if (!tempToken) return;

    try {
        const { data } = await axiosInstance.post<UserTokenResponse>(
            "/users/admin_panel/get_token/",
            { token: tempToken }
        );
        if (data?.token) {
            localStorage.setItem(AUTH_TOKEN_KEY, data.token);
            localStorage.setItem(LAST_LOGIN_KEY, new Date().toISOString());
        }
    } catch {
        // Exchange failures (expired, single-use already consumed, etc.) must
        // not block boot — fall through so the user lands on the unauthed
        // path and can sign in normally.
    } finally {
        stripInboundTempToken();
    }
};
